import { AlertTriangle } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { BOLT, fmtMoney, units } from '@/lib/format'

/** GET /accounting/summary → `bridge`: cash in → recognized revenue, line by line. */
export interface BridgeLine {
  key: string
  label: string
  rub: string
}

export interface AccountingBridgeCardProps {
  lines: BridgeLine[]
}

const SUB = new Set(['revenue_access', 'revenue_consumption', 'revenue_reversals'])

/**
 * «Поступления → выручка» (docs/accounting.md «Мост»): what came in, minus refunds, plus partner
 * USDT turned into ⚡, minus the growth of unspent ⚡ (a liability, not revenue yet) = recognized
 * revenue, then its parts. «Не объяснено журналом» is 0 normally; a non-zero value is called out
 * in words, not by colour alone (rule 21).
 */
export function AccountingBridgeCard({ lines }: AccountingBridgeCardProps) {
  const other = lines.find((l) => l.key === 'other')
  const unexplained = other !== undefined && Number(other.rub) !== 0
  return (
    <section>
      <SectionHeader
        as="h3"
        title="Поступления → выручка"
        description={`Почему оплаченное за период не равно выручке: возвраты, переводы партнёрских USDT и неизрасходованные ${BOLT}.`}
      />
      <Card>
        <dl className="flex flex-col gap-2 text-body">
          {lines.map((l) => {
            if (l.key === 'other' && !unexplained) return null
            const total = l.key === 'revenue'
            return (
              <div
                key={l.key}
                className={
                  total
                    ? 'mt-1 flex justify-between gap-3 border-t border-hairline pt-2'
                    : 'flex justify-between gap-3'
                }
              >
                <dt className={SUB.has(l.key) ? 'min-w-0 pl-4 text-caption text-ink-3' : total ? 'min-w-0 text-ink-1' : 'min-w-0 text-ink-2'}>
                  {units(l.label)}
                </dt>
                <dd className="tnum shrink-0 text-right text-ink-1">{fmtMoney(l.rub, 'RUB')}</dd>
              </div>
            )
          })}
        </dl>
        {unexplained && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-caption text-ink-2">
            <StatusBadge tone="warn" icon={<AlertTriangle />}>
              Не сходится
            </StatusBadge>
            Есть сумма, не объяснённая журналом: платежи до учёта выручки или суммы без снимка в рублях.
          </p>
        )}
      </Card>
    </section>
  )
}
